import React, {
    createContext, useContext, useEffect, useState, useRef
} from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { useLazyQuery } from '@apollo/client';
import { TrackingContext } from '@vrbo/react-event-tracking';
import { mapDispatchToProps, mapStateToPropsContext } from './properties';
import { GET_EXTERNAL_PROPERTY } from './Graphql/query';
import log from '../../utils/ClientLogger';
import { createInteractionEvent, createOutcomeEvent } from '../../utils/EventProviders/EgClickstreamUtil';
import { isValidUrl, looksLikeUrlAttempt, invalidUrlsRegex } from './Components/utils/invalidUrls';

export const CALL_STATUS = {
    // input is being filled
    WRITING: 'writing',
    // call found property
    SUCCESS: 'success',
    // call could not find property
    NOT_FOUND: 'not_found',
    // invalid url
    ERROR_INVALID: 'error_invalid',
    ERROR_PASTE_URL: 'error_paste_url',
    // unsupported site
    ERROR_UNSUPPORTED: 'error_unsupported',
    // empty link, call status empty
    EMPTY: 'empty',
    // user rejects found property
    REJECTED: 'rejected',
    // call is in progress
    LOADING: 'loading',
    // failed gql call
    FAILURE: 'failure',
    // prefilled link
    PREFILLED: 'prefilled'
};

const createNotFoundErrors = (errorCode) => ({
    [errorCode]: {
        code: errorCode,
        status: CALL_STATUS.NOT_FOUND
    }
});

const createObjectFromArray = (array) => {
    const rtnObj = {};
    array.forEach((item) => {
        if (Object.keys(item)[0]) {
            rtnObj[Object.keys(item)[0]] = item[Object.keys(item)[0]];
        }
    });
    return rtnObj;
};

export const ERROR_CODES = {
    ...createObjectFromArray([
        3003,
        3004,
        7100,
        8001,
        9000
    ].map((code) => createNotFoundErrors(code))),
    UNSUPPORTED_SITE: {
        code: 'UNSUPPORTED_SITE',
        status: CALL_STATUS.ERROR_UNSUPPORTED
    },
    INVALID_URL: {
        code: 'INVALID_URL',
        status: CALL_STATUS.ERROR_INVALID
    },
    PASTE_URL: {
        code: 'PASTE_URL',
        status: CALL_STATUS.ERROR_PASTE_URL
    },
    FAILURE: {
        code: 'FAILURE',
        status: CALL_STATUS.FAILURE
    }
};

export const ModalContext = createContext();
export const CompetitorUrlContext = createContext();
export const CallStatusContext = createContext();
export const CompetitorUrlPropertyContext = createContext();

export function CompetitorUrlComponentContext({
    children,
    setCompetitorUrl: setCompetitorUrlAction,
    setCompetitorUrlLoading,
    competitorUrl: joinLink
}) {
    const { trigger } = useContext(TrackingContext);

    const [loading, setLoading] = useState(false);
    const [showDialog, setShowDialog] = useState(false);
    const [competitorUrl, setCompetitorUrl] = useState('');
    const [callStatus, setCallStatus] = useState(CALL_STATUS.EMPTY);
    const [competitorUrlProperty, setCompetitorUrlProperty] = useState({});
    const [error, setError] = useState(null);

    const validationTimeoutRef = useRef(null);

    const onCallError = (callError) => {
        log(['error'], 'CompetitorUrlContext', callError);
        setCallStatus(CALL_STATUS.FAILURE);
    };
    const onCallCompleted = (payload) => {
        const {
            error: resultError,
            data
        } = payload.fetchExternalPropertyInformation;
        if (resultError) {
            if (ERROR_CODES[resultError.code]) {
                log(['info'], 'CompetitorUrlContext', resultError);
                setCallStatus(ERROR_CODES[resultError.code].status);
            } else {
                onCallError(resultError.code);
            }
        }
        if (data) {
            const {
                propertyName,
                address,
                country,
                state,
                city,
                zipCode,
                imageUrl,
                externalId
            } = data;
            setCallStatus(CALL_STATUS.SUCCESS);
            setCompetitorUrl(externalId);
            setCompetitorUrlProperty({
                title: propertyName,
                addressLine1: address,
                city,
                state,
                country,
                zipCode,
                imageUrl
            });
        }
    };

    const [getPropertyInformation, queryState] = useLazyQuery(GET_EXTERNAL_PROPERTY, {
        onCompleted: onCallCompleted,
        onError: onCallError
    });

    useEffect(() => {
        setLoading(queryState.loading);
    }, [queryState.loading]);

    // call operations
    const executeCall = () => {
        trigger('interaction', createInteractionEvent('add_comp_link.submitted', 'CompetitorUrlWidget', 'add-link'));
        if (!isValidUrl(competitorUrl)) {
            setCallStatus(ERROR_CODES.INVALID_URL.status);
            return;
        }
        getPropertyInformation({ variables: { id: competitorUrl } });
    };
    const executeRemove = () => {
        trigger('interaction', createInteractionEvent('remove_comp_link.selected', 'CompetitorUrlWidget', 'remove-link'));
        setCompetitorUrlAction('');
        setCallStatus(CALL_STATUS.EMPTY);
    };
    const handleCompetitorUrlChange = (value) => {
        setCallStatus(CALL_STATUS.WRITING);
        setCompetitorUrl(value);

        setError(null);

        if (validationTimeoutRef.current) {
            clearTimeout(validationTimeoutRef.current);
        }
        // debounce validation for better copy-paste experience
        if (!value || value.trim().length === 0) {
            return;
        }
        validationTimeoutRef.current = setTimeout(() => {
            const trimmedValue = value.trim();

            if (trimmedValue.length > 0) {
                const isValid = isValidUrl(trimmedValue);
                if (!isValid) {
                    // If it looks like a URL attempt OR matches a restricted URL,
                    // show PASTE_URL error
                    if (looksLikeUrlAttempt(trimmedValue) || invalidUrlsRegex.test(trimmedValue)) {
                        setCallStatus(ERROR_CODES.PASTE_URL.status);
                    } else {
                        // For clearly garbage entries that don't look like URLs,
                        // show INVALID_URL error
                        setCallStatus(ERROR_CODES.INVALID_URL.status);
                    }
                }
            }
        }, 500);
    };

    // handle call status changes
    useEffect(() => {
        setLoading(callStatus === CALL_STATUS.LOADING);
        switch (callStatus) {
        case CALL_STATUS.EMPTY:
            setCompetitorUrl('');
            setCompetitorUrlProperty({});
            setShowDialog(false);
            break;
        case CALL_STATUS.SUCCESS:
        case CALL_STATUS.NOT_FOUND:
            setShowDialog(true);
            break;
        case CALL_STATUS.REJECTED:
            setCallStatus(CALL_STATUS.EMPTY);
            break;
        case CALL_STATUS.ERROR_INVALID:
        case CALL_STATUS.ERROR_PASTE_URL:
        case CALL_STATUS.ERROR_UNSUPPORTED:
        case CALL_STATUS.FAILURE:
            setShowDialog(false);
            break;
        case CALL_STATUS.WRITING:
            setCompetitorUrlProperty({});
            setShowDialog(false);
            break;
        default:
            break;
        }
    }, [callStatus]);

    // modal operations
    const closeModal = () => {
        setShowDialog(false);
    };
    const confirmPropertyHandler = () => {
        trigger('interaction', createInteractionEvent('confirm_comp_link.selected', 'CompetitorUrlWidget', 'confirm-link'));
        setCompetitorUrlAction(competitorUrl);
        closeModal();
    };
    const rejectPropertyHandler = () => {
        trigger('interaction', createInteractionEvent('incorrect_comp_link.selected', 'CompetitorUrlWidget', 'not-my-link'));
        setCallStatus(CALL_STATUS.REJECTED);
    };
    const propertyNotFoundHandler = () => {
        trigger('interaction', createInteractionEvent('next_button.selected', 'CompetitorUrlWidget', 'not-found-link'));
        setCallStatus(CALL_STATUS.EMPTY);
        closeModal();
    };

    useEffect(() => {
        setCompetitorUrlLoading(loading);
    }, [loading]);

    useEffect(() => {
        if (joinLink && joinLink !== competitorUrl) {
            setCompetitorUrl(joinLink);
            setCallStatus(CALL_STATUS.PREFILLED);
        }
    }, [joinLink]);

    useEffect(() => {
        switch (callStatus) {
        case CALL_STATUS.SUCCESS:
            trigger('outcome', createOutcomeEvent('property_map.succeeded', 'CompetitorUrlWidget', competitorUrl));
            break;
        case CALL_STATUS.NOT_FOUND:
            trigger('outcome', createOutcomeEvent('property_map.failed', 'CompetitorUrlWidget', competitorUrl));
            break;
        default:
            break;
        }
    }, [callStatus]);

    useEffect(() => () => {
        if (validationTimeoutRef.current) {
            clearTimeout(validationTimeoutRef.current);
        }
    }, []);

    return (
        <ModalContext.Provider value={{
            showDialog,
            confirmPropertyHandler,
            rejectPropertyHandler,
            propertyNotFoundHandler
        }}
        >
            <CompetitorUrlContext.Provider
                value={{ competitorUrl, setCompetitorUrl: handleCompetitorUrlChange }}
            >
                <CallStatusContext.Provider value={{
                    callStatus,
                    error,
                    setError,
                    executeRemove,
                    executeCall,
                    loading
                }}
                >
                    <CompetitorUrlPropertyContext.Provider value={{ competitorUrlProperty }}>
                        {children}
                    </CompetitorUrlPropertyContext.Provider>
                </CallStatusContext.Provider>
            </CompetitorUrlContext.Provider>
        </ModalContext.Provider>
    );
}

CompetitorUrlComponentContext.propTypes = {
    children: PropTypes.node.isRequired,
    setCompetitorUrl: PropTypes.func.isRequired,
    setCompetitorUrlLoading: PropTypes.func.isRequired,
    competitorUrl: PropTypes.string.isRequired
};

CompetitorUrlComponentContext.displayName = 'CompetitorUrlComponentContext';

export default connect(mapStateToPropsContext, mapDispatchToProps)(CompetitorUrlComponentContext);
